import * as THREE from "three";
import { buildVehicleVisual, type VehicleVisual } from "../rendering/VehicleRenderer.js";
import type { VehicleBlueprint } from "@neon-arena/shared";

export class VehicleShowcase {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private currentVisual: VehicleVisual | null = null;
  private animFrame = 0;
  private angle = 0;
  private currentIdx = 0;
  private readonly blueprints: VehicleBlueprint[];
  private readonly onSelectCb: (idx: number) => void;

  constructor(
    canvas: HTMLCanvasElement,
    blueprints: VehicleBlueprint[],
    onSelect: (idx: number) => void,
  ) {
    this.blueprints = blueprints;
    this.onSelectCb = onSelect;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 200);
    this.camera.position.set(0, 3.5, 9);
    this.camera.lookAt(0, 0.5, 0);

    // Cyan key light from front-top
    const key = new THREE.DirectionalLight(0x22d3ee, 2.8);
    key.position.set(4, 9, 7);
    this.scene.add(key);

    // Orange fill from rear
    const fill = new THREE.DirectionalLight(0xfb923c, 1.2);
    fill.position.set(-5, 2, -6);
    this.scene.add(fill);

    // White rim from directly behind
    const rim = new THREE.DirectionalLight(0xffffff, 0.9);
    rim.position.set(0, 6, -9);
    this.scene.add(rim);

    // Soft ambient
    const ambient = new THREE.AmbientLight(0xffffff, 0.3);
    this.scene.add(ambient);

    if (blueprints.length > 0) this.loadVehicle(0);
  }

  private loadVehicle(idx: number): void {
    if (this.currentVisual) {
      this.scene.remove(this.currentVisual.group);
    }
    this.currentIdx = idx;
    const bp = this.blueprints[idx];
    this.currentVisual = buildVehicleVisual(bp, "solid");
    this.currentVisual.group.position.y = 0;
    this.scene.add(this.currentVisual.group);
    // Start at a pleasing 3/4 front angle
    this.angle = Math.PI * 0.15;
    this.onSelectCb(idx);
  }

  goTo(idx: number): void {
    const n = this.blueprints.length;
    const clamped = ((idx % n) + n) % n;
    if (clamped !== this.currentIdx) this.loadVehicle(clamped);
  }

  next(): void { this.goTo(this.currentIdx + 1); }
  prev(): void { this.goTo(this.currentIdx - 1); }
  getIdx(): number { return this.currentIdx; }

  private syncSize(): void {
    const canvas = this.renderer.domElement;
    const w = canvas.clientWidth || 400;
    const h = canvas.clientHeight || 260;
    if (this.renderer.domElement.width !== w || this.renderer.domElement.height !== h) {
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
  }

  start(): void {
    if (this.animFrame) return;
    this.syncSize();
    let last = performance.now();
    const tick = (): void => {
      this.animFrame = requestAnimationFrame(tick);
      const now = performance.now();
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      this.angle += 0.45 * dt;
      if (this.currentVisual) {
        this.currentVisual.group.rotation.y = this.angle;
      }
      this.syncSize();
      this.renderer.render(this.scene, this.camera);
    };
    tick();
  }

  stop(): void {
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = 0;
    }
  }

  destroy(): void {
    this.stop();
    this.renderer.dispose();
  }
}
